import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { getUnsubscribe, confirmUnsubscribe, resubscribe } from "../api/email";

// Where "Unsubscribe" in a Birdie Bus email lands. No sign-in: the link
// itself is the proof. It asks before acting, because some company mail
// filters open every link in an email and would otherwise unsubscribe people
// who never clicked.

export default function Unsubscribe() {
  const [params] = useSearchParams();
  const token = params.get("t") || "";
  const [state, setState] = useState({ loading: true });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token || token === "test") {
      setState({ error: token === "test" ? "This is the link from a test email, so there's nothing to unsubscribe." : "This link is missing its code." });
      return;
    }
    getUnsubscribe(token)
      .then((r) => setState({ email: r.email, unsubscribed: r.unsubscribed }))
      .catch(() => setState({ error: "This link isn't valid. Reply to any of our emails and we'll take you off the list." }));
  }, [token]);

  async function act(fn, unsubscribed) {
    setBusy(true);
    try {
      await fn(token);
      setState((s) => ({ ...s, unsubscribed, changed: true }));
    } catch {
      setState((s) => ({ ...s, failed: true }));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-birdie-beige py-10 px-4">
      <div className="mx-auto max-w-md text-center">
        <img
          src="/birdie-bus-logo.png"
          alt="Birdie Bus"
          className="h-16 w-auto mx-auto mb-6"
          onError={(e) => {
            e.currentTarget.style.display = "none";
          }}
        />
        <div className="bg-white rounded-2xl border border-birdie-green/15 shadow-sm p-6">
          {state.loading ? (
            <p className="text-birdie-charcoal/60 py-6">Loading…</p>
          ) : state.error ? (
            <p className="text-birdie-charcoal/80 py-4">{state.error}</p>
          ) : state.unsubscribed ? (
            <>
              <div className="text-3xl mb-2">👋</div>
              <h1 className="font-accent text-xl text-birdie-charcoal">You're unsubscribed</h1>
              <p className="text-sm text-birdie-charcoal/65 mt-2">
                We won't email <strong>{state.email}</strong> again. Thanks for playing on the Birdie Bus!
              </p>
              <button
                type="button"
                onClick={() => act(resubscribe, false)}
                disabled={busy}
                className="mt-5 text-sm font-semibold text-birdie-green underline disabled:opacity-50"
              >
                Oops, keep me on the list
              </button>
            </>
          ) : (
            <>
              <h1 className="font-accent text-xl text-birdie-charcoal">
                {state.changed ? "You're back on the list" : "Unsubscribe from Birdie Bus emails?"}
              </h1>
              <p className="text-sm text-birdie-charcoal/65 mt-2">
                {state.changed ? (
                  <>Welcome back! We'll keep emails occasional.</>
                ) : (
                  <>
                    We'll stop emailing <strong>{state.email}</strong>.
                  </>
                )}
              </p>
              {!state.changed && (
                <button
                  type="button"
                  onClick={() => act(confirmUnsubscribe, true)}
                  disabled={busy}
                  className="mt-5 w-full rounded-xl bg-birdie-green text-white font-semibold px-6 py-3 hover:bg-birdie-green-dark disabled:opacity-50"
                >
                  {busy ? "One moment…" : "Unsubscribe"}
                </button>
              )}
            </>
          )}
          {state.failed && (
            <p className="text-sm text-red-700 mt-3">That didn't go through. Please try again in a moment.</p>
          )}
        </div>
        <p className="text-xs text-birdie-charcoal/45 mt-4">
          <a href="https://birdiebus.com" className="underline">
            birdiebus.com
          </a>
        </p>
      </div>
    </div>
  );
}
